import { CanceledError } from 'axios'
import CommonShippingFees from 'common/components/entities/ShippingFees'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import {
  SelectedShipping,
  ShippingFeesInterface,
} from 'common/types/entities/ShippingFeesInterface'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import {
  managementSelectors,
  useManagement,
  useOptIn,
  usePage,
  usePayment,
} from 'publisher/store'
import {
  getCustomerCountry,
  getFieldValueBySlug,
} from 'publisher/store/optIn/optInSelectors'
import pageSelectors from 'publisher/store/page/pageSelectors'
import { setSelectedShipping } from 'publisher/store/payment/paymentActions'
import paymentSelectors, {
  getCheckedBump,
  getSelectedShipping,
} from 'publisher/store/payment/paymentSelectors'
import {
  doesShippingServicesHaveShippingMethod,
  findShippingServiceByRuleId,
} from 'publisher/utils/shippingFees'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getShippingRules } from 'src/publisher/api/shipping-api'
import { mockedPhysicalProduct } from 'src/shared/mocks/product'
import { mockedShippingResponse } from 'src/shared/mocks/shipping'
import { ShippingResponse } from 'src/shared/types/pricing-types'

interface ShippingFeesProps {
  entity: ShippingFeesInterface
}

const ShippingFees = ({ entity }: ShippingFeesProps) => {
  const dispatch = useDispatch()
  let product = usePayment(paymentSelectors.getProduct)
  const productVariant = usePayment(paymentSelectors.getProductActiveVariant)
  const bumpProduct = usePayment(getCheckedBump)
  const coupon = usePayment(paymentSelectors.getCheckedCoupon)
  const quantity = usePayment(paymentSelectors.getProductQuantity)
  const customerCountry = useOptIn(
    state =>
      getFieldValueBySlug(state, FieldSlugEnum.Country) ||
      getCustomerCountry(state),
  )

  const isScreenshotMode = useManagement(managementSelectors.isScreenshotMode)
  const isSsr = typeof window === 'undefined'
  const isPreview = useManagement(managementSelectors.isPreview)
  const isTemplate = usePage(pageSelectors.getIsPageTemplate)
  const isUsingMockedData =
    isTemplate || (!product && isPreview) || isScreenshotMode

  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController>()
  const [shippingData, setShippingData] = useState<
    ShippingResponse | undefined
  >(isUsingMockedData ? mockedShippingResponse : undefined)
  const selectedShipping = usePayment(getSelectedShipping)
  const handleShippingSelect = async (shipping: SelectedShipping) => {
    dispatch(setSelectedShipping(shipping))
  }

  if (isUsingMockedData) {
    product = mockedPhysicalProduct
  }

  useEffect(() => {
    if (isUsingMockedData || !product || !customerCountry) {
      return
    }

    const fetchShipping = async () => {
      try {
        setIsLoading(true)
        abortControllerRef.current = new AbortController()

        const { data } = await getShippingRules(
          {
            country: customerCountry,
            product: product.id,
            productVariant: productVariant?.id,
            coupon: coupon?.id,
            bumpProduct: bumpProduct?.product?.id,
            quantity,
          },
          abortControllerRef.current.signal,
        )

        setShippingData(data)

        if (
          !doesShippingServicesHaveShippingMethod(
            data.shippingServices,
            selectedShipping,
          )
        ) {
          // Try to preserve the user's shipping service selection by finding the same rule
          const previousService = selectedShipping
            ? findShippingServiceByRuleId(
                data.shippingServices,
                selectedShipping.ruleId,
              )
            : null

          const nextService = previousService ?? data.shippingServices[0]

          if (nextService) {
            const rule =
              nextService.rules.find(r => r.id === selectedShipping?.ruleId) ??
              nextService.rules[0]
            const condition = rule.shippingConditions[0]
            if (condition) {
              await handleShippingSelect({
                ruleId: rule.id,
                currency: rule.currency,
                conditionId: condition.id,
                price: condition.price,
              })
            } else {
              await handleShippingSelect({
                ruleId: rule.id,
                currency: rule.currency,
                price: rule.price,
              })
            }
          } else {
            dispatch(setSelectedShipping(null))
          }
        }

        setIsLoading(false)
      } catch (err) {
        if (err instanceof CanceledError) {
          // do nothing
        } else {
          setIsLoading(false)
        }
      }
    }

    if (abortControllerRef.current?.signal) {
      abortControllerRef.current.abort('new_request_sent')
    }

    fetchShipping()
  }, [
    customerCountry,
    product?.id,
    coupon?.id,
    bumpProduct?.product?.id,
    productVariant,
    quantity,
  ])

  if ((isSsr && !isScreenshotMode) || !product) {
    return (
      <StyleWrapper
        margin={entity.margin}
        appearance={entity.appearance}
        mobileMargin={entity.mobileMargin}
      />
    )
  }

  return (
    <StyleWrapper
      margin={entity.margin}
      mobileMargin={entity.mobileMargin}
      appearance={entity.appearance}
    >
      <CommonShippingFees
        isLoading={isLoading}
        selectedShipping={selectedShipping}
        customerCountry={customerCountry}
        onChange={handleShippingSelect}
        entity={entity}
        shipping={shippingData}
      />
    </StyleWrapper>
  )
}

export default ShippingFees
